import { LIBRARY_REVIEWED, STANDARD_DISCLAIMER } from './taxonomy'
import type { RegulatoryRecord } from './types'

const reviewed = LIBRARY_REVIEWED
const disclaimer = STANDARD_DISCLAIMER

/**
 * Published records only. Each entry was checked against the cited official source
 * on the last-reviewed date. Unverified topics live in researchQueue.ts and are
 * not rendered as law.
 */
export const REGULATORY_RECORDS: RegulatoryRecord[] = [
  {
    id: 'uae-fdl-11-2024',
    slug: 'federal-decree-law-11-2024-climate-change',
    title: 'Federal Decree-Law No. (11) of 2024 on the Reduction of Climate Change Effects',
    shortTitle: 'Federal Decree-Law No. 11 of 2024',
    authority: 'United Arab Emirates — Ministry of Climate Change and Environment is the federal ministry named in the Decree-Law',
    jurisdiction: ['federal', 'free_zones'],
    categories: ['climate', 'ghg'],
    instrumentType: 'federal_decree_law',
    summary:
      'Federal climate framework that applies to Sources in the State, including free zones. It sets mitigation and adaptation objectives, requires Sources to contribute to emission reductions, and limits detailed measurement, inventory and periodic reporting duties to Sources determined by the Ministry and the competent authority. It is not a rule that every UAE company must file an annual greenhouse-gas report.',
    requirementsSummary: [
      'Article 3: the Decree-Law applies to Sources in the State, including free zones.',
      'Article 1 defines Sources as public and private legal persons, and individual enterprises, whose operations or activities release greenhouse gases.',
      'Article 4: Sources shall contribute to reducing their emissions toward climate neutrality, through one or more listed means. The Ministry may later issue controls and standards for those means.',
      'Article 5: the Cabinet, on a Ministry proposal, determines national sectoral emission-reduction targets. That is a government target-setting duty, not a company filing form.',
      'Article 6: Sources determined by the Ministry and the competent authority must measure emissions, prepare an inventory, submit periodic reports to the standards those authorities specify, submit activity and reduction data, and keep measurement records for five years.',
      'Article 6 does not, in the reviewed text, name a universal calendar filing day or deem every licensee a determined Source.',
      'Article 7 assigns adaptation planning to the competent authority and concerned entities in specified sectors, coordinated with the Ministry.',
      'Article 10(3): the Ministry shall establish and manage the National Carbon Credit Registry.',
      'Article 15 sets a criminal fine range for Sources that violate Article 6(1). Article 17 says administrative penalties are to be set later by Cabinet resolution.',
    ],
    applicabilitySummary:
      'Potentially relevant to persons whose UAE activities release greenhouse gases, including in free zones. Detailed MRV duties arise only after the Ministry and the competent authority determine the Source. UAE incorporation or a trade licence alone is not treated here as that determination.',
    applicabilityConditions: [
      'The person falls within the Article 1 definition of a Source.',
      'For measurement, inventory, periodic reports and the five-year record duty, the Source has been determined by the Ministry and the competent authority in coordination with the concerned entity.',
      'Competent authority means the local authority concerned in each emirate, including free zones.',
      'No company-specific filing calendar was identified in the Decree-Law text reviewed.',
    ],
    practicalImplications: [
      'Map whether any activity releases greenhouse gases and which emirate competent authority is relevant, including free-zone authorities.',
      'Do not build a compliance position that assumes an annual federal GHG return is already due from every company.',
      'Watch for Ministry or competent-authority determinations, forms and later resolutions before locking a reporting calendar.',
      'Keep any emissions measurements that are already made, because determined Sources must retain records for five years.',
    ],
    recommendedActions: [
      'Ask legal and operations owners whether any site has been determined, notified or registered for Article 6 measurement and reporting.',
      'Identify the competent authority for each emirate or free zone where activities take place.',
      'If a notice exists, review its text before adopting any deadline. This navigator does not validate notices.',
      'Separate this framework from exchange ESG guidance and from customer carbon questionnaires.',
    ],
    entityTypes: [
      'Public and private legal persons whose activities release greenhouse gases',
      'Individual enterprises whose activities release greenhouse gases',
      'Sources in free zones',
      'Sources later determined by the Ministry and the competent authority for MRV',
    ],
    obligationLevel: 'conditional',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'both',
    sectors: ['cross_sector'],
    publicationDate: '2024-08-28',
    effectiveDate: '2025-05-30',
    deadlines: [
      {
        date: '2024-08-28',
        label: 'Issued date stated on the official legislation portal',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://uaelegislation.gov.ae/en/legislations/2558',
        notes: 'This is the issue date, not the entry-into-force date. Official Gazette date on the portal is 30 August 2024 (Gazette No. 782).',
      },
      {
        date: '2025-05-30',
        label: 'Entry into force. Article 21: nine months after publication. Portal effective date: 30 May 2025.',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://uaelegislation.gov.ae/en/legislations/2558',
        notes: 'Entry into force is not a company filing deadline. No universal annual return date was identified.',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'UAE Legislation portal — Federal Decree-Law on the Reduction of Climate Change Effects',
        url: 'https://uaelegislation.gov.ae/en/legislations/2558',
        sourceType: 'primary',
        relevantSection: 'Issued 28 Aug 2024; effective 30 May 2025; Gazette No. 782 dated 30 Aug 2024; status Active',
      },
      {
        title: 'Official English text download — Federal Decree-Law No. (11) of 2024',
        url: 'https://uaelegislation.gov.ae/en/legislations/2558/download',
        sourceType: 'primary',
        relevantSection: 'Articles 1, 3, 4, 5, 6, 7, 10, 15, 17 and 21',
      },
    ],
    relatedRecordIds: ['uae-cab-67-2024', 'uae-net-zero-2050', 'ghg-protocol-corporate'],
    relevantSections: ['Article 1', 'Article 3', 'Article 4', 'Article 5', 'Article 6', 'Article 7', 'Article 10', 'Article 15', 'Article 21'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. Status, dates and Article 6 designation limit checked against the legislation portal and the official English text.',
        sourceUrl: 'https://uaelegislation.gov.ae/en/legislations/2558',
      },
    ],
    disclaimer,
  },
  {
    id: 'uae-cab-67-2024',
    slug: 'cabinet-resolution-67-2024-national-carbon-credit-register',
    title: 'Cabinet Resolution No. (67) of 2024 concerning the National Register for Carbon Credits',
    shortTitle: 'Cabinet Resolution No. 67 of 2024',
    authority: 'UAE Cabinet — administered by the Ministry of Climate Change and Environment, with Securities and Commodities Authority competences for in-State trading platforms',
    jurisdiction: ['federal', 'free_zones'],
    categories: ['carbon_registration', 'ghg', 'climate'],
    instrumentType: 'cabinet_resolution',
    summary:
      'Establishes the National Register for Carbon Credits and an MRV regime for two groups: entities of huge carbon emissions, and participating entities that register voluntarily. Huge emissions means Scope 1 and Scope 2 emissions equal to or above 0.5 million metric tons of CO2 equivalent a year. The resolution applies in the State, including financial and non-financial free zones. It does not impose that register on every UAE company.',
    requirementsSummary: [
      'Article 3(1): applies to entities at or above 0.5 million metric tons CO2e a year within Scope 1 and Scope 2.',
      'Article 3(2): entities below that level may participate voluntarily. Once they register to obtain or trade credits, the resolution applies to them.',
      'Article 3(4): the Cabinet may later add sectors or change the threshold. No such amending resolution was verified for this record.',
      'Article 4: in-scope entities must monitor, report and verify emissions. The reviewed text requires the GHG emissions report to be delivered annually to the Ministry and the competent authority. It does not state a calendar day.',
      'Article 4 also ties verification bodies to ISO standards cited in the resolution and to Ministry authorisation.',
      'Article 6: entities of huge carbon emissions shall register and submit the documents listed, including a valid commercial or industrial licence and facility-level reduction information.',
      'Article 9: carbon credits issued by the register are treated as financial instruments when traded in the State. The Securities and Commodities Authority is assigned licensing and market-supervision competences. A separate SCA resolution number was not verified for this record.',
    ],
    applicabilitySummary:
      'Conditionally mandatory for entities at or above the 0.5 million tCO2e Scope 1 and Scope 2 threshold, and for entities that choose to participate. Sector, headcount or a trade licence does not prove the threshold.',
    applicabilityConditions: [
      'Annual Scope 1 plus Scope 2 emissions are at least 0.5 million metric tons CO2e, or the entity has voluntarily joined the register.',
      'Activities are in the UAE, including financial and non-financial free zones.',
      'The annual report duty has no confirmed calendar deadline in the text reviewed.',
      'Scope 3 is not part of the threshold definition reviewed.',
    ],
    practicalImplications: [
      'A company cannot confirm this resolution from sector alone. It needs a Scope 1 and Scope 2 inventory against the threshold.',
      'Voluntary registration is a choice that then brings the resolution’s MRV conditions with it.',
      'Credit trading inside the UAE is a financial-markets question as well as a climate question.',
    ],
    recommendedActions: [
      'Prepare or commission a Scope 1 and Scope 2 inventory before concluding whether the 0.5 million tCO2e threshold is met.',
      'If near the threshold or already trading credits, confirm registration steps with the Ministry and read the annex on inventory standards.',
      'Do not adopt a filing day that the resolution does not state.',
      'If trading is planned, check current SCA requirements separately. They are not reproduced here.',
    ],
    entityTypes: [
      'Entities of huge carbon emissions (at or above 0.5 million tCO2e Scope 1 and 2)',
      'Participating entities that voluntarily register',
      'Carbon-credit trading platforms in the State',
    ],
    obligationLevel: 'conditional',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'both',
    sectors: ['cross_sector', 'energy', 'oil_gas', 'manufacturing', 'waste', 'aviation'],
    publicationDate: '2024-06-10',
    effectiveDate: '2024-12-28',
    deadlines: [
      {
        date: '2024-06-10',
        label: 'Issued date on the official legislation portal',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://uaelegislation.gov.ae/en/legislations/2521',
        notes: 'Official Gazette date on the portal is 28 June 2024, Gazette No. 778. Issue date is not the effective date.',
      },
      {
        date: '2024-12-28',
        label: 'Entry into force stated on the official legislation portal',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://uaelegislation.gov.ae/en/legislations/2521',
      },
      {
        label: 'Annual GHG emissions report to the Ministry and the competent authority for entities of huge emissions and participating entities',
        type: 'filing',
        certainty: 'confirmed',
        sourceUrl: 'https://uaelegislation.gov.ae/en/legislations/2521',
        notes: 'Article 4 requires annual delivery. No calendar day was identified in the reviewed text. Do not treat the effective date as a filing deadline.',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'UAE Legislation portal — Cabinet Resolution concerning the National Register for Carbon Credits',
        url: 'https://uaelegislation.gov.ae/en/legislations/2521',
        sourceType: 'primary',
        relevantSection: 'Issued 10 Jun 2024; effective 28 Dec 2024; Gazette No. 778; status Active',
      },
    ],
    relatedRecordIds: ['uae-fdl-11-2024', 'ghg-protocol-corporate'],
    relevantSections: ['Article 1', 'Article 2', 'Article 3', 'Article 4', 'Article 5', 'Article 6', 'Article 9'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description:
          'Initial navigator record. Threshold, free-zone scope, annual report duty and absence of a calendar day checked against the official portal and the English text of Cabinet Resolution No. 67 of 2024.',
        sourceUrl: 'https://uaelegislation.gov.ae/en/legislations/2521',
      },
    ],
    disclaimer,
  },
  {
    id: 'uae-fl-12-2018-waste',
    slug: 'federal-law-12-2018-integrated-waste-management',
    title: 'Federal Law No. (12) of 2018 on Integrated Waste Management',
    shortTitle: 'Federal Law No. 12 of 2018',
    authority: 'United Arab Emirates — competent authorities designated for waste management, with the Ministry of Climate Change and Environment in the federal framework',
    jurisdiction: ['federal', 'free_zones'],
    categories: ['waste', 'environment'],
    instrumentType: 'federal_law',
    summary:
      'Federal framework for waste from generation through segregation, collection, storage, reuse, recycling, treatment and disposal inside the State, including free zones. Nuclear and radioactive waste is excluded. It allocates operational responsibility to the competent authority and places duties on waste producers and other actors. It is not a single nationwide filing form with one confirmed calendar deadline.',
    requirementsSummary: [
      'The law aims to regulate waste management and safe disposal to protect the environment and human health.',
      'It applies to waste in the State, including free zones, excluding nuclear and radioactive waste.',
      'The competent authority is responsible for waste management in its geographic scope.',
      'The official text covers liability of waste producers, suppliers and entities, and specific streams including municipal solid waste, sewage, hazardous waste, construction and demolition waste, industrial waste, agricultural waste, marine waste and oil waste.',
      'It provides for a national waste database and for liability for environmental damage.',
      'Permits, facility standards and many operational details sit with the competent authority and the executive regulation. A single executive-regulation record was not verified for publication in this dataset.',
    ],
    applicabilitySummary:
      'Potentially relevant to persons who produce, handle, transport, treat or dispose of waste in the UAE, including free zones. Which permit or manifest applies depends on the waste stream and the local competent authority.',
    applicabilityConditions: [
      'The activity involves waste covered by the law.',
      'Nuclear and radioactive waste is outside this law.',
      'Local competent-authority rules and the executive regulation may add conditions that are not restated here.',
    ],
    practicalImplications: [
      'Office and industrial sites both generate waste, but the control intensity differs sharply for hazardous, industrial and construction waste.',
      'Free-zone location does not, under this law’s scope clause, take waste outside the federal framework.',
      'Do not assume one federal annual waste return. Check the competent authority for the site.',
    ],
    recommendedActions: [
      'List waste streams by site and flag hazardous, industrial and construction waste.',
      'Identify the competent authority for each emirate or free zone.',
      'Confirm permit and transport conditions before moving hazardous waste.',
    ],
    entityTypes: ['Waste producers', 'Waste handlers, transporters and treatment operators', 'Entities in free zones generating covered waste'],
    obligationLevel: 'conditional',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'both',
    sectors: ['cross_sector', 'manufacturing', 'construction', 'healthcare', 'waste', 'oil_gas'],
    publicationDate: '2018-12-18',
    effectiveDate: '2019-03-31',
    deadlines: [
      {
        date: '2018-12-18',
        label: 'Issued date on the official legislation portal',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://uaelegislation.gov.ae/en/legislations/1164',
        notes: 'Official Gazette date on the portal is 31 December 2018, Gazette No. 644.',
      },
      {
        date: '2019-03-31',
        label: 'Entry into force on the official legislation portal. The law enters into force three months after publication.',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://uaelegislation.gov.ae/en/legislations/1164',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'UAE Legislation portal — Federal Law on Integrated Waste Management',
        url: 'https://uaelegislation.gov.ae/en/legislations/1164',
        sourceType: 'primary',
        relevantSection: 'Issued 18 Dec 2018; effective 31 Mar 2019; status Active',
      },
      {
        title: 'Official English text — Federal Law No. (12) of 2018',
        url: 'https://uaelegislation.gov.ae/en/legislations/1164/download',
        sourceType: 'primary',
        relevantSection: 'Scope, waste-producer liability and waste streams',
      },
      {
        title: 'UAE Government portal — waste management',
        url: 'https://u.ae/en/information-and-services/environment-and-energy/waste-management',
        sourceType: 'secondary',
        relevantSection: 'Overview pointing to Federal Law No. 12 of 2018 and an executive regulation',
      },
    ],
    relatedRecordIds: ['uae-fl-24-1999-environment'],
    relevantSections: ['Scope of application', 'Waste producer liability', 'Hazardous wastes', 'National waste database'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record from the legislation portal. Executive-regulation details were not copied from secondary summaries.',
        sourceUrl: 'https://uaelegislation.gov.ae/en/legislations/1164',
      },
    ],
    disclaimer,
  },
  {
    id: 'uae-fl-24-1999-environment',
    slug: 'federal-law-24-1999-environment',
    title: 'Federal Law No. (24) of 1999 on the Protection and Development of the Environment',
    shortTitle: 'Federal Law No. 24 of 1999',
    authority: 'United Arab Emirates — federal environmental framework, now read with later amendments and with the competent authorities',
    jurisdiction: ['federal'],
    categories: ['environment', 'water', 'biodiversity'],
    instrumentType: 'federal_law',
    summary:
      'Principal federal environmental law. Its stated aims include protecting environmental quality, controlling pollution, developing natural resources, preserving biological diversity, and protecting human health. The Ministry-hosted English text records an amendment by Federal Law No. 11 of 2006. Project-permitting and emission limits are not self-contained in a single company filing calendar.',
    requirementsSummary: [
      'Article 2 objectives include pollution control, protection of biological diversity and natural resources, and protection of human health.',
      'The law addresses development projects, the water environment, air pollution, soil, hazardous substances and waste, and protected areas, with duties distributed between the federal agency named in the 1999 text and local competent authorities.',
      'Many numerical limits and procedures were reserved to the executive regulations.',
      'The English text on the Ministry site states that the law was amended by Federal Law No. 11 of 2006 dated 7 May 2006. Article-level effects of that amendment were not re-verified line by line for this record.',
      'A precise Official Gazette day, and therefore a calculated effective date, was not confirmed beyond the issue date of 17 October 1999 and publication in Official Gazette No. 340.',
    ],
    applicabilitySummary:
      'Potentially relevant to projects and facilities that may pollute or affect land, air, water or biodiversity in the UAE. Whether a permit is required depends on the activity and the competent authority. It is not evidence that every office must hold an environmental permit.',
    applicabilityConditions: [
      'The activity is of a kind the law and its executive regulations regulate, such as polluting projects, discharges, hazardous materials or impacts on the marine or terrestrial environment.',
      'Local competent authorities administer much of the permitting.',
      'The 2006 amendment must be read with the 1999 law. This record does not restate that amendment article by article.',
    ],
    practicalImplications: [
      'Industrial, construction, energy and marine activities should identify permit and impact-assessment triggers with the local authority.',
      'Service businesses without those activities should not treat this record as a confirmed permit obligation.',
      'Biodiversity and water duties in this law are framework duties. Later protected-area and water instruments are tracked separately in the research queue until verified.',
    ],
    recommendedActions: [
      'For each facility, ask whether an environmental permit, impact study or discharge consent is already in place.',
      'Read the executive regulations (Cabinet Resolution No. 37 of 2001) with the law, not instead of it.',
      'Use the Arabic text if a compliance position depends on a specific article.',
    ],
    entityTypes: [
      'Project owners and facility operators whose activities may pollute or affect the environment',
      'Handlers of hazardous substances and wastes addressed by the law',
    ],
    obligationLevel: 'conditional',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['cross_sector', 'manufacturing', 'construction', 'energy', 'oil_gas', 'waste'],
    publicationDate: '1999-10-17',
    deadlines: [
      {
        date: '1999-10-17',
        label: 'Issue date of Federal Law No. 24 of 1999',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://moccae.gov.ae/assets/download/788830bc/L24-99%20Eng.pdf.aspx',
        notes: 'Published in Official Gazette No. 340. The law text says it comes into force three months after publication. The exact gazette day was not confirmed, so no effective date is stored.',
      },
      {
        label: 'Entry into force three months after publication in the Official Gazette. Calendar day not confirmed in this review.',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://moccae.gov.ae/assets/download/788830bc/L24-99%20Eng.pdf.aspx',
      },
    ],
    status: 'amended',
    officialSources: [
      {
        title: 'Ministry of Climate Change and Environment — English text of Federal Law No. 24 of 1999',
        url: 'https://moccae.gov.ae/assets/download/788830bc/L24-99%20Eng.pdf.aspx',
        sourceType: 'primary',
        relevantSection: 'Issue date 17 October 1999; amendment note for Federal Law No. 11 of 2006; Article 2',
      },
    ],
    relatedRecordIds: ['uae-cab-37-2001-environment', 'uae-fl-12-2018-waste'],
    relevantSections: ['Article 2', 'Development and the environment', 'Water environment', 'Hazardous substances and waste'],
    amendments: [
      {
        date: '2006-05-07',
        label: 'Amended by Federal Law No. 11 of 2006, as noted on the Ministry English text. Article-level changes were not separately reviewed.',
        sourceUrl: 'https://moccae.gov.ae/assets/download/788830bc/L24-99%20Eng.pdf.aspx',
      },
    ],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description:
          'Initial navigator record. Issue date and 2006 amendment note taken from the Ministry English text. Effective calendar day left blank because the gazette day was not confirmed.',
        sourceUrl: 'https://moccae.gov.ae/assets/download/788830bc/L24-99%20Eng.pdf.aspx',
      },
    ],
    disclaimer,
  },
  {
    id: 'uae-cab-37-2001-environment',
    slug: 'cabinet-resolution-37-2001-environment-executive-regulations',
    title: 'Cabinet Resolution No. (37) of 2001 on the executive regulations of Federal Law No. (24) of 1999',
    shortTitle: 'Cabinet Resolution No. 37 of 2001',
    authority: 'UAE Cabinet',
    jurisdiction: ['federal'],
    categories: ['environment', 'water', 'biodiversity'],
    instrumentType: 'executive_regulation',
    summary:
      'Executive regulations for Federal Law No. 24 of 1999 on environmental protection. The official portal lists the resolution as active, issued on 9 December 2001 and effective on 31 December 2001. The schedules contain technical systems that have to be read with the law. This record does not copy emission limits or permit thresholds from secondary summaries.',
    requirementsSummary: [
      'The portal title is the systems of the executive regulation of Federal Law No. 24 of 1999.',
      'Portal status is active.',
      'Technical tables sit in annexes. They were not extracted into numeric limits in this record.',
    ],
    applicabilitySummary:
      'Relevant together with Federal Law No. 24 of 1999 where an activity is regulated by those executive systems. It does not create a new universal corporate reporting deadline.',
    applicabilityConditions: [
      'The underlying environmental law applies to the activity.',
      'The relevant annex of the executive regulation covers that activity.',
    ],
    practicalImplications: [
      'Permit and standard questions should be checked in the annexes and with the competent authority, not answered from this summary.',
    ],
    recommendedActions: [
      'Open the official portal record and the annexes before relying on a numeric limit.',
      'Pair this resolution with Federal Law No. 24 of 1999 and any later amendment.',
    ],
    entityTypes: ['Persons whose activities are covered by the executive systems of the 1999 environment law'],
    obligationLevel: 'conditional',
    listingScope: 'not_specific',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['manufacturing', 'construction', 'energy', 'oil_gas', 'waste'],
    publicationDate: '2001-12-09',
    effectiveDate: '2001-12-31',
    deadlines: [
      {
        date: '2001-12-09',
        label: 'Issued date on the UAE Legislation portal',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://uaelegislation.gov.ae/ar/legislations/1151',
      },
      {
        date: '2001-12-31',
        label: 'Effective date and Official Gazette date on the portal (Gazette No. 374)',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://uaelegislation.gov.ae/ar/legislations/1151',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'UAE Legislation portal (Arabic) — Cabinet Resolution No. 37 of 2001',
        url: 'https://uaelegislation.gov.ae/ar/legislations/1151',
        sourceType: 'primary',
        relevantSection: 'Issued 9 Dec 2001; effective 31 Dec 2001; status active',
      },
    ],
    relatedRecordIds: ['uae-fl-24-1999-environment'],
    relevantSections: ['Portal status and dates', 'Annexes (not extracted)'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record limited to portal identity, status and dates. Numeric annex limits were not copied.',
        sourceUrl: 'https://uaelegislation.gov.ae/ar/legislations/1151',
      },
    ],
    disclaimer,
  },
  {
    id: 'uae-mohre-mr-44-2022',
    slug: 'ministerial-resolution-44-2022-ohs-midday-break',
    title: 'Ministerial Resolution No. (44) of 2022 regarding Occupational Health and Safety and Labour Accommodation',
    shortTitle: 'MOHRE Resolution No. 44 of 2022',
    authority: 'Ministry of Human Resources and Emiratisation (MOHRE)',
    jurisdiction: ['federal'],
    categories: ['labour'],
    instrumentType: 'ministerial_decision',
    summary:
      'Occupational health and safety resolution whose Article 3 bans work performed directly under the sun and in open places between 12:30 pm and 3:00 pm from 15 June to 15 September every year. The Ministry’s consolidated text says Article 3 was amended by Ministerial Resolution No. 516 of 2024. Exemptions for technically continuous work are left to the procedures manual. This is a worker-protection rule, not an emissions-reporting rule.',
    requirementsSummary: [
      'Article 3: no work directly under the sun and in open places from 12:30 pm to 3:00 pm between 15 June and 15 September each year.',
      'Work is not to resume before 3:00 pm, subject to the procedures manual.',
      'The procedures manual specifies exempted processes that must continue for technical reasons, and the employer obligations in those cases.',
      'The Ministry’s public guidance repeats the same dates and hours and describes employer obligations during the break.',
      'The consolidated download states that the article was amended by Ministerial Resolution No. 516 of 2024. The standalone 2024 resolution was not separately reviewed.',
    ],
    applicabilitySummary:
      'Likely relevant where workers perform outdoor work under the sun in open places during the summer window. Free-zone employment arrangements can differ and need a separate check. Office-only work with no outdoor component has no outdoor-ban trigger in the text reviewed.',
    applicabilityConditions: [
      'Work is performed directly under the sun and in open places.',
      'The calendar window is 15 June to 15 September every year, from 12:30 pm to 3:00 pm.',
      'Technical exemptions are those in the procedures manual, not assumptions.',
      'Application inside a particular free zone should be confirmed if workers are not under the MOHRE regime.',
    ],
    practicalImplications: [
      'Construction, agriculture, field operations and outdoor logistics need a summer work plan.',
      'The duty recurs annually. It is not a one-off 2022 deadline.',
      'Indoor office work is not the activity described in Article 3.',
    ],
    recommendedActions: [
      'Identify roles that work outdoors between 15 June and 15 September.',
      'Schedule the 12:30 pm to 3:00 pm break and keep the procedures-manual position for any claimed exemption.',
      'Use the Ministry’s midday-break guidance as the public explanation and the resolution as the legal text.',
    ],
    entityTypes: ['Employers of workers who work under the sun in open places'],
    obligationLevel: 'mandatory',
    listingScope: 'not_specific',
    financialScope: 'non_financial',
    establishmentScope: 'not_specific',
    sectors: ['construction', 'logistics', 'agriculture', 'oil_gas', 'real_estate'],
    publicationDate: '2022-02-03',
    deadlines: [
      {
        date: '2022-02-03',
        label: 'Issue date printed on the Ministry consolidated text',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://mohre.gov.ae/assets/download/4f4a2f6a/ministerial-resolution-no-44-of-2022-and-its-amendments.aspx',
      },
      {
        date: '2026-06-15',
        label: 'Start of the 2026 midday-break window (12:30 pm–3:00 pm, outdoor work)',
        type: 'compliance',
        certainty: 'confirmed',
        sourceUrl: 'https://www.mohre.gov.ae/en/media-center/awareness-and-guidance/midday-break.aspx',
        notes: 'Annual rule. The 2026 window had started before this record was reviewed.',
      },
      {
        date: '2026-09-15',
        label: 'End of the 2026 midday-break window',
        type: 'compliance',
        certainty: 'confirmed',
        sourceUrl: 'https://www.mohre.gov.ae/en/media-center/awareness-and-guidance/midday-break.aspx',
      },
      {
        date: '2027-06-15',
        label: 'Start of the next midday-break window under the annual rule',
        type: 'compliance',
        certainty: 'confirmed',
        sourceUrl: 'https://mohre.gov.ae/assets/download/4f4a2f6a/ministerial-resolution-no-44-of-2022-and-its-amendments.aspx',
        notes: 'Next occurrence of the recurring 15 June date. Not a newly issued one-off deadline.',
      },
    ],
    status: 'amended',
    officialSources: [
      {
        title: 'MOHRE — Ministerial Resolution No. 44 of 2022 and its amendments (consolidated text)',
        url: 'https://mohre.gov.ae/assets/download/4f4a2f6a/ministerial-resolution-no-44-of-2022-and-its-amendments.aspx',
        sourceType: 'primary',
        relevantSection: 'Article 3 Noon Work Ban; amendment note for Resolution No. 516 of 2024',
      },
      {
        title: 'MOHRE — Midday Break guidance',
        url: 'https://www.mohre.gov.ae/en/media-center/awareness-and-guidance/midday-break.aspx',
        sourceType: 'primary',
        relevantSection: 'Dates 15 June–15 September; hours 12:30 pm–3:00 pm',
      },
    ],
    relatedRecordIds: [],
    relevantSections: ['Article 3'],
    amendments: [
      {
        label: 'Ministry consolidated text states that Article 3 was amended by Ministerial Resolution No. 516 of 2024. The standalone amending resolution was not separately reviewed.',
        sourceUrl: 'https://mohre.gov.ae/assets/download/4f4a2f6a/ministerial-resolution-no-44-of-2022-and-its-amendments.aspx',
      },
    ],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. Annual window taken from Article 3 and the Ministry guidance page. Resolution 516 of 2024 noted only as an amendment flag.',
        sourceUrl: 'https://www.mohre.gov.ae/en/media-center/awareness-and-guidance/midday-break.aspx',
      },
    ],
    disclaimer,
  },
]
